/**
 * Resource Deletion Service
 *
 * This service handles tracking of deleted AI-generated resources
 * to prevent them from being regenerated in future AI calls.
 *
 * Storage Strategy:
 * - Firebase Firestore: Persistent storage across browser sessions and devices
 * - In-Memory Map: Fast reads and immediate consistency
 * - Hybrid Sync: Write to both, read from memory, initialize from Firebase
 */

import { db, doc, getDoc, updateDoc } from '@trip-planner-monorepo/shared/firebase/firebaseInit';

// In-memory cache for fast reads (synced with Firebase)
// Structure: Map<placeKey, Array<{url, name, category, deletedAt}>>
const deletedResources = new Map();

/**
 * Marks a resource as deleted for a specific place
 * @param {string} userId - User ID
 * @param {string} tripId - Trip ID
 * @param {string} placeId - Place ID
 * @param {string} resourceUrl - URL of the deleted resource
 * @param {Object} resourceData - Full resource data (optional)
 */
export function markResourceAsDeleted(userId, tripId, placeId, resourceUrl, resourceData = {}) {
  const key = `${userId}:${tripId}:${placeId}`;

  if (!deletedResources.has(key)) {
    deletedResources.set(key, []);
  }

  // Store full resource information for potential reinstatement
  const deletedResource = {
    url: resourceUrl,
    name: resourceData.name || 'Resource',
    category: resourceData.category || 'general',
    deletedAt: new Date().toISOString()
  };

  // Add to deleted resources array if not already present
  const existingResources = deletedResources.get(key);
  if (!existingResources.some(r => r.url === resourceUrl)) {
    existingResources.push(deletedResource);
  }

  console.log(`🗑️ [ResourceDeletion] Marked resource as deleted: ${resourceUrl}`);
}

/**
 * Checks if a resource has been deleted for a specific place
 * @param {string} userId - User ID
 * @param {string} tripId - Trip ID
 * @param {string} placeId - Place ID
 * @param {string} resourceUrl - URL to check
 * @returns {boolean} True if the resource has been deleted
 */
export function isResourceDeleted(userId, tripId, placeId, resourceUrl) {
  const key = `${userId}:${tripId}:${placeId}`;
  const deletedArray = deletedResources.get(key);

  if (!deletedArray) {
    return false;
  }

  return deletedArray.some(r => r.url === resourceUrl);
}

/**
 * Filters out deleted resources from a list
 * @param {string} userId - User ID
 * @param {string} tripId - Trip ID
 * @param {string} placeId - Place ID
 * @param {Array} resources - Array of resource objects with 'url' property
 * @returns {Array} Filtered array without deleted resources
 */
export function filterDeletedResources(userId, tripId, placeId, resources) {
  return resources.filter(resource => 
    !isResourceDeleted(userId, tripId, placeId, resource.url)
  );
}

/**
 * Clears all deleted resources for a specific place
 * @param {string} userId - User ID
 * @param {string} tripId - Trip ID
 * @param {string} placeId - Place ID
 */
export function clearDeletedResources(userId, tripId, placeId) {
  const key = `${userId}:${tripId}:${placeId}`;
  deletedResources.delete(key);
  console.log(`🗑️ [ResourceDeletion] Cleared deleted resources for place: ${placeId}`);
}

/**
 * Gets all deleted resources for a specific place
 * @param {string} userId - User ID
 * @param {string} tripId - Trip ID
 * @param {string} placeId - Place ID
 * @returns {Array} Array of deleted resource objects
 */
export function getDeletedResources(userId, tripId, placeId) {
  const key = `${userId}:${tripId}:${placeId}`;
  const deletedArray = deletedResources.get(key);

  return deletedArray ? [...deletedArray] : [];
}

/**
 * Gets deleted resource URLs for a specific place (for backward compatibility)
 * @param {string} userId - User ID
 * @param {string} tripId - Trip ID
 * @param {string} placeId - Place ID
 * @returns {Array} Array of deleted resource URLs
 */
export function getDeletedResourceUrls(userId, tripId, placeId) {
  const deletedArray = getDeletedResources(userId, tripId, placeId);
  return deletedArray.map(r => r.url);
}

/**
 * Reinstates a deleted resource (removes from deleted list)
 * @param {string} userId - User ID
 * @param {string} tripId - Trip ID
 * @param {string} placeId - Place ID
 * @param {string} resourceUrl - URL of the resource to reinstate
 * @returns {Object|null} The reinstated resource or null if not found
 */
export function reinstateResource(userId, tripId, placeId, resourceUrl) {
  const key = `${userId}:${tripId}:${placeId}`;
  const deletedArray = deletedResources.get(key);

  if (!deletedArray) {
    console.warn(`⚠️ [ResourceDeletion] No deleted resources found for place: ${placeId}`);
    return null;
  }

  const index = deletedArray.findIndex(r => r.url === resourceUrl);
  if (index === -1) {
    console.warn(`⚠️ [ResourceDeletion] Resource not found in deleted list: ${resourceUrl}`);
    return null;
  }

  // Remove from deleted resources
  const [reinstated] = deletedArray.splice(index, 1);
  console.log(`♻️ [ResourceDeletion] Reinstated resource: ${resourceUrl}`);

  return reinstated;
}

// ============================================================================
// FIREBASE PERSISTENCE FUNCTIONS (Place Document Storage)
// ============================================================================

/**
 * Loads deleted resources from place document in Firebase
 * Initializes the in-memory Map with persisted data
 *
 * @param {string} userId - User ID
 * @param {string} tripId - Trip ID
 * @param {string} placeId - Place ID
 * @returns {Promise<Array>} Array of deleted resource objects
 */
export async function loadDeletedResourcesFromFirebase(userId, tripId, placeId) {
  try {
    console.log(`📥 [ResourceDeletion] Loading deleted resources from place document: ${placeId}`);

    // Get place document
    const placeRef = doc(db, 'users', userId, 'trips', tripId, 'places', placeId);
    const placeDoc = await getDoc(placeRef);

    if (!placeDoc.exists()) {
      console.log(`⚠️ [ResourceDeletion] Place document not found: ${placeId}`);
      return [];
    }

    const placeData = placeDoc.data();
    const resources = placeData.deletedAIResources || [];

    // Update in-memory cache
    const key = `${userId}:${tripId}:${placeId}`;
    deletedResources.set(key, resources);

    console.log(`✅ [ResourceDeletion] Loaded ${resources.length} deleted resources from place document`);
    return resources;

  } catch (error) {
    console.error('❌ [ResourceDeletion] Failed to load from Firebase:', error);
    // Return empty array on error (graceful degradation)
    return [];
  }
}

/**
 * Marks a resource as deleted with Firebase persistence
 * Updates both place document and in-memory storage
 *
 * @param {string} userId - User ID
 * @param {string} tripId - Trip ID
 * @param {string} placeId - Place ID
 * @param {string} resourceUrl - URL of the deleted resource
 * @param {Object} resourceData - Full resource data (optional)
 * @returns {Promise<boolean>} True if successful
 */
export async function markResourceAsDeletedWithPersistence(userId, tripId, placeId, resourceUrl, resourceData = {}) {
  try {
    // First update in-memory (immediate consistency)
    markResourceAsDeleted(userId, tripId, placeId, resourceUrl, resourceData);

    // Get current deleted resources from in-memory
    const key = `${userId}:${tripId}:${placeId}`;
    const allDeletedResources = deletedResources.get(key) || [];

    // Update place document with complete deleted resources array
    const placeRef = doc(db, 'users', userId, 'trips', tripId, 'places', placeId);
    await updateDoc(placeRef, {
      deletedAIResources: allDeletedResources,
      deletedAIResourcesUpdatedAt: new Date().toISOString()
    });

    console.log(`💾 [ResourceDeletion] Persisted deletion to place document: ${resourceUrl}`);
    return true;

  } catch (error) {
    console.error('❌ [ResourceDeletion] Failed to persist to place document:', error);
    // In-memory update already succeeded, so don't fail the operation
    return false;
  }
}

/**
 * Reinstates a deleted resource with Firebase persistence
 * Removes from both place document and in-memory storage
 *
 * @param {string} userId - User ID
 * @param {string} tripId - Trip ID
 * @param {string} placeId - Place ID
 * @param {string} resourceUrl - URL of the resource to reinstate
 * @returns {Promise<Object|null>} The reinstated resource or null if not found
 */
export async function reinstateResourceWithPersistence(userId, tripId, placeId, resourceUrl) {
  try {
    // First update in-memory (immediate consistency)
    const reinstated = reinstateResource(userId, tripId, placeId, resourceUrl);

    if (!reinstated) {
      return null;
    }

    // Get updated deleted resources from in-memory (after removal)
    const key = `${userId}:${tripId}:${placeId}`;
    const remainingDeletedResources = deletedResources.get(key) || [];

    // Update place document with remaining deleted resources
    const placeRef = doc(db, 'users', userId, 'trips', tripId, 'places', placeId);
    await updateDoc(placeRef, {
      deletedAIResources: remainingDeletedResources,
      deletedAIResourcesUpdatedAt: new Date().toISOString()
    });

    console.log(`💾 [ResourceDeletion] Removed from place document: ${resourceUrl}`);
    return reinstated;

  } catch (error) {
    console.error('❌ [ResourceDeletion] Failed to update place document:', error);
    // In-memory update already succeeded, continue with operation
    return reinstated;
  }
}

/**
 * Clears all deleted resources for a place with Firebase persistence
 * Removes from both place document and in-memory storage
 *
 * @param {string} userId - User ID
 * @param {string} tripId - Trip ID
 * @param {string} placeId - Place ID
 * @returns {Promise<boolean>} True if successful
 */
export async function clearDeletedResourcesWithPersistence(userId, tripId, placeId) {
  try {
    // Get count before clearing (for logging)
    const key = `${userId}:${tripId}:${placeId}`;
    const resourceCount = (deletedResources.get(key) || []).length;

    // First clear in-memory
    clearDeletedResources(userId, tripId, placeId);

    // Then clear from place document
    const placeRef = doc(db, 'users', userId, 'trips', tripId, 'places', placeId);
    await updateDoc(placeRef, {
      deletedAIResources: [],
      deletedAIResourcesUpdatedAt: new Date().toISOString()
    });

    console.log(`💾 [ResourceDeletion] Cleared ${resourceCount} resources from place document: ${placeId}`);
    return true;

  } catch (error) {
    console.error('❌ [ResourceDeletion] Failed to clear from place document:', error);
    return false;
  }
}

/**
 * Fetches all deleted resources for a place from place document (direct query)
 * Does not update in-memory cache - use loadDeletedResourcesFromFirebase() for that
 *
 * @param {string} userId - User ID
 * @param {string} tripId - Trip ID
 * @param {string} placeId - Place ID
 * @returns {Promise<Array>} Array of deleted resource objects
 */
export async function fetchDeletedResourcesFromFirebase(userId, tripId, placeId) {
  try {
    const placeRef = doc(db, 'users', userId, 'trips', tripId, 'places', placeId);
    const placeDoc = await getDoc(placeRef);

    if (!placeDoc.exists()) {
      return [];
    }

    const placeData = placeDoc.data();
    return placeData.deletedAIResources || [];

  } catch (error) {
    console.error('❌ [ResourceDeletion] Failed to fetch from place document:', error);
    return [];
  }
}
